import { EOrgAccess } from '../../domains/identity';
import { ITenant } from '../../domains/inventory/';
import { EProductCodes } from '../../domains/inventory';
import { UserEnvironmentObject, UserEnvironmentValuesType } from './types';

export class OrgEnvironment implements UserEnvironmentObject {
  constructor(propsObject: UserEnvironmentObject) {
    Object.assign(this, propsObject);
  }

  userOrgData = [];
  userRoleData = [];
  tenantsData: ITenant[] = [];
  xmCloudContext = undefined;

  get _hasOneOrg() {
    return this.userOrgData.length === 1;
  }

  get _hasManyOrgs() {
    return this.userOrgData.length > 1;
  }

  get _hasOrg() {
    return this.userOrgData.length > 0;
  }

  get _userOrgData() {
    return this.userOrgData;
  }

  get isOrgUser() {
    const isUser = Boolean(
      this.userRoleData.filter((item: any) =>
        [EOrgAccess['Organization User']].includes(item.role),
      ).length > 0,
    );
    return !this.isOrgAdminOwner && isUser;
  }

  get withNoHeadlessCMS() {
    return Boolean(
      this.tenantsData.filter((t) => {
        return t.labels?.ProductCode === EProductCodes.HeadlessCMS;
      }).length === 0,
    );
  }

  get isOrgAdminOwner() {
    return Boolean(
      this.userRoleData.filter((item: any) =>
        [
          EOrgAccess['Organization Admin'],
          EOrgAccess['Organization Owner'],
        ].includes(item.role),
      ).length > 0,
    );
  }

  get isOrgOwner() {
    return Boolean(
      this.userRoleData.filter((item: any) =>
        [EOrgAccess['Organization Owner']].includes(item.role),
      ).length > 0,
    );
  }

  get isOrgAdmin() {
    return (
      Boolean(
        this.userRoleData.filter((item: any) =>
          [EOrgAccess['Organization Admin']].includes(item.role),
        ).length > 0,
      ) && !this.isOrgOwner
    );
  }

  get withNoTenants() {
    return this.tenantsData.length === 0;
  }

  get withNoXMenvironment() {
    return !this.withXMCloudContext;
  }

  get withOnlyXMTenant() {
    return this.tenantsData.length === 1 && this.hasXMTenant;
  }

  get hasXMTenant() {
    return Boolean(
      this.tenantsData.filter(
        (t) => t?.labels.ProductCode === EProductCodes.XMCloud,
      ).length,
    );
  }

  get withXMCloudContext() {
    return Boolean(this.xmCloudContext);
  }

  get withManyXMTenants() {
    return Boolean(
      this.tenantsData.filter(
        (t) => t?.labels.ProductCode === EProductCodes.XMCloud,
      ).length > 1,
    );
  }

  get withXMTenantAndAtLeastOneMoreTenant() {
    return (
      this.hasXMTenant &&
      Array.from([
        ...new Set(this.tenantsData.map((t) => t.labels.ProductCode)),
      ]).length > 1
    );
  }

  get hasAssignedProducts() {
    return !!this.tenantsData.length;
  }

  values = (): UserEnvironmentValuesType => {
    const env = {
      ORG_ADMIN: this.isOrgAdmin,
      ORG_OWNER: this.isOrgOwner,
      ORG_ADMIN_OR_OWNER: this.isOrgAdminOwner,
      ORG_ADMIN_OWNER: this.isOrgAdminOwner,
      ORG_USER: this.isOrgUser,
      WITH_NO_ORG: !this._hasOrg,
      WITH_AT_LEAST_ONE_ORG: this._hasOrg,
      WITH_ONE_ORG: this._hasOneOrg,
      WITH_MANY_ORG: this._hasManyOrgs,
      WITH_XM_CLOUD_CONTEXT: this.withXMCloudContext,
      WITH_NO_XM_TENANT: this.withNoXMenvironment,
      WITH_XM_TENANT: this.hasXMTenant,
      WITH_XM_TENANT_ONLY: this.withOnlyXMTenant,
      WITH_NO_TENANTS: this.withNoTenants,
      WITH_MANY_XM_TENANTS: this.withManyXMTenants,
      WITH_XM_TENANT_AND_AT_LEAST_ONE_MORE_TENANT:
        this.withXMTenantAndAtLeastOneMoreTenant,
      WITH_NO_HEADLESS: this.withNoHeadlessCMS,
      WITH_APP_ACCESS: this.hasAssignedProducts,
    };

    const data = {
      tenants: this.tenantsData,
    };

    const LABELS = Object.keys(env).filter((attr) =>
      Boolean(env[attr as keyof typeof env]),
    );

    return { ...env, LABELS, data };
  };
}
